import { useEffect, useRef, useState } from "react";

import { Box, Button, Center, Flex, Spinner } from "@chakra-ui/react";

import { useLoggedOut } from "~/auth/Layout";
import { useNavigate, useProduct, useQueryParam } from "~/hooks";
import { useMutateWithException } from "~/hooks/useMutateApi";
import useNamespace from "~/hooks/useNamespace";
import { sendEvent, EventType, FeatureType } from "~/services/amplitude";
import { Alert } from "~/theme/components";
import ArrowLeft from "~/theme/icons/navigation/ArrowLeft";
import { addOnboardingSkipParam } from "~/utils/url";

import CreatePassword from "./components/CreatePassword";

const ENDPOINT = import.meta.env.VITE_API_URL as string;

function Activate() {
  const { brand_color, callback_url } = useProduct();
  const { goBack } = useLoggedOut();
  const navigate = useNavigate();
  const query = useQueryParam("email", "product", "trail");
  const { translate } = useNamespace("activate");
  const [signInLoading, setSignInLoading] = useState<boolean>(false);
  const [password, setPassword] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [passwordState, setPasswordState] = useState<"success" | "error" | undefined>();
  const [errorVerifyCodeState, setErrorVerifyCodeState] = useState<"noError" | "error" | undefined>("noError");
  const { auth } = useLoggedOut();

  const [confirmPassword, _] = useMutateWithException("auth/activate/password/", {
    baseURL: ENDPOINT,
    method: "PATCH",
  });

  const didValidate = useRef(false);

  const validateEmail = (email: string) => {
    if (checkPattEmail(email)) {
      fetch(`${ENDPOINT}/auth/activate/verify/?email=${email}`).then((res) => {
        if (res.status == 303 || res.status == 404)
          navigate(`/signin/?email=${encodeURIComponent(email)}${query.trail ? `&trail=${query.trail}` : ""}`);
      });
    }
  };

  useEffect(() => {
    if (query.email && !didValidate.current) {
      didValidate.current = true;
      validateEmail(query.email);
    }
  }, [validateEmail, query.email]);

  const checkPattEmail = (email: string) => {
    const re =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

    return re.test(email.toLowerCase());
  };

  const createPasswordAndSignin = async (email: string, password: string) => {
    setSignInLoading(true);

    try {
      const data = await confirmPassword({ email: email, password: password });

      if (data?.status && data?.status == 200) {
        setPasswordState("success");
        setPassword(password);
        setEmail(email);
      } else if (data?.status && data?.status == 404) {
        navigate("/signin/");
      }
    } catch (error) {
      setSignInLoading(false);
      setPasswordState("error");

      const message = (error as Error).message;

      sendEvent({
        event: EventType.INPUT_ERROR_OCCURRED,
        feature: FeatureType.ACTIVATION,
        aditionalProps: { message: message },
      });
      if (message === "Request failed with status code 404") {
        navigate(`/signin/?email=${encodeURIComponent(email)}`);
        setErrorVerifyCodeState("error");
      }
    }
  };

  useEffect(() => {
    async function signIn() {
      if (passwordState === "success") {
        if (email && password) {
          const redirect = query.product ? callback_url : undefined;

          try {
            const modifiedRedirect = query.trail
              ? `?trail=${query.trail}`
              : redirect && query.product
              ? addOnboardingSkipParam(redirect, query.product)
              : undefined;

            await auth.handleSignIn(email, password, modifiedRedirect);
          } catch {
            setSignInLoading(false);
            setPasswordState("error");
          }
        }
      }
    }

    signIn();
  }, [passwordState, auth, email, password, query.product, query.trail, callback_url]);

  if (signInLoading || auth.loading) {
    return (
      <Center width="100%" height="100%">
        <Spinner boxSize="12" thickness="0.25rem" color="purple.300" margin="auto" />
      </Center>
    );
  }

  if (passwordState === "success") {
    return (
      <>
        <Button variant="terciary" leftIcon={<ArrowLeft />} onClick={goBack}>
          {translate("activate@back")}
        </Button>
        <Flex direction="column" alignItems="center">
          <Box as="p" textStyle="h5" color={brand_color}>
            {translate("activate@redirect-title")}
          </Box>
          <Box textStyle="body2" color="gray.700" my="4">
            {translate("activate@redirect-subtitle")}
          </Box>
          <Spinner boxSize="12" thickness="0.25rem" color={brand_color} />
        </Flex>
      </>
    );
  }

  return (
    <>
      <Flex maxWidth="520px" direction="column" alignItems="flex-start" gridRowGap="8" width="100%">
        <CreatePassword loading={signInLoading} createPassword={createPasswordAndSignin} changeEmail={validateEmail} />
        {passwordState === "error" && (
          <Alert status="error">
            {errorVerifyCodeState === "error" && translate("activate@create-password-error")}
          </Alert>
        )}
      </Flex>
    </>
  );
}

export default Activate;
